import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { TrendingSidebar } from "../components/TrendingSidebar";
import { CATEGORY_LABELS } from "../lib/constants";

// Section heads are themselves capsules: a floating white pill for the title,
// a soft violet pill for the link. No rules, no hard edges.
function SectionHeader({ title, to }) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
            <h2 className="inline-flex items-center gap-3 rounded-full bg-surface border border-line shadow-card pl-4 pr-6 py-2.5 font-display text-lg sm:text-xl font-semibold tracking-[-0.01em]">
                <span className="w-2.5 h-2.5 rounded-full bg-accent-sheen" />
                {title}
            </h2>
            <Link
                to={to}
                className="group inline-flex items-center gap-1.5 shrink-0 rounded-full bg-accent-soft px-5 py-2.5 text-[13px] font-semibold text-accent hover:bg-accent hover:text-accent-foreground transition-colors duration-200"
            >
                View all
                <ArrowRight size={13} className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
        </div>
    );
}

function HomeSkeleton() {
    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <p className="inline-flex items-center gap-2 rounded-full bg-surface-raised px-4 py-2 font-mono text-xs text-dim mb-7">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-dot" />
                Loading the newsroom…
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                <div className="lg:col-span-8 rounded-2xl bg-surface border border-line shadow-card p-4">
                    <div className="shimmer aspect-video !rounded-xl" />
                    <div className="shimmer h-9 w-3/4 mt-6" />
                    <div className="shimmer h-4 w-1/2 mt-4" />
                </div>
                <div className="lg:col-span-4 rounded-2xl bg-surface-raised p-4 space-y-5">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex gap-4">
                            <div className="flex-1 space-y-2.5">
                                <div className="shimmer h-4 w-20 !rounded-full" />
                                <div className="shimmer h-4 w-full" />
                                <div className="shimmer h-4 w-2/3" />
                            </div>
                            <div className="shimmer w-[92px] h-[76px] shrink-0" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Home() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/home").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <HomeSkeleton />;
    }

    if (!data || !data.featured) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="home-empty">
                <div className="panel max-w-lg mx-auto p-12 text-center">
                    <span className="eyebrow inline-flex rounded-full bg-accent-soft px-3.5 py-1.5 text-accent">Standby</span>
                    <h2 className="font-display text-2xl font-semibold tracking-[-0.01em] mt-5">Fetching the latest stories</h2>
                    <p className="mt-3 text-sm text-dim leading-relaxed">
                        Articles are being pulled from The Guardian. Please check back in a moment.
                    </p>
                </div>
            </div>
        );
    }

    const { featured, latest, trending, sections } = data;

    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="home-page">
            {/* Hero pillow + the Latest rail on its own pastel plate */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                <div className="lg:col-span-8">
                    <ArticleCard article={featured} variant="hero" />
                </div>
                <div className="lg:col-span-4 rounded-2xl bg-surface-raised p-4 sm:p-5">
                    <div className="flex items-center gap-2.5 px-3 pb-4">
                        <span className="w-2 h-2 rounded-full bg-accent animate-pulse-dot" />
                        <h2 className="eyebrow text-foreground">Latest News</h2>
                    </div>
                    <div className="space-y-1">
                        {latest.slice(0, 6).map((a) => (
                            <ArticleCard key={a.id} article={a} variant="compact" />
                        ))}
                    </div>
                </div>
            </div>

            {/* Category sections + trending sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 mt-20">
                <div className="lg:col-span-8 space-y-16">
                    {Object.entries(sections).map(([cat, arts]) => (
                        <section key={cat} data-testid={`home-section-${cat}`}>
                            <SectionHeader title={CATEGORY_LABELS[cat] || cat} to={`/category/${cat}`} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {arts.map((a) => (
                                    <ArticleCard key={a.id} article={a} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
                <div className="lg:col-span-4">
                    <TrendingSidebar items={trending} />
                </div>
            </div>
        </div>
    );
}
